import { memo, useCallback, useEffect, useState } from 'react';
import { array, bool, func, instanceOf, number, object, oneOfType, string } from 'prop-types';
import { DayPicker, DayPickerTabs, isDayAfter, isSelectingFirstDay, lastDateForSearch, mobileNumberOfMonths, useComponentVisibleHook } from '../index';
import { addDays } from '../utils';
import { TextField } from '../../index';
import { CalendarIcon } from './Styled';
import responsive from '../../../decorators/responsive';
import Modal from './ModalLayout';
import ResponsiveValue from './ResponsiveInput';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import DropdownAnimation from '../../../animations/DropdownAnimation';
const tabsList = [{
  key: 'single',
  title: I18n.t('pages.search.round_trip.datepicker.direct')
}, {
  key: 'range',
  title: I18n.t('pages.search.round_trip.datepicker.return')
}];
const addTwoDaysFromDay = date => ({
  from: date,
  to: addDays({
    date,
    count: 2
  })
});
const reverseTwoDaysFromDay = date => ({
  from: addDays({
    date,
    count: -2
  }),
  to: date
});
const datesRange = {
  from: null,
  to: null
};
const DatepickerInput = ({
  isRange = false,
  isTabs = false,
  labelInputName,
  selectedDaysSingle = new Date(),
  inputValue = '',
  selectedDaysRange = {
    from: new Date(),
    to: new Date()
  },
  disabledDays = [],
  numberOfMonths = 1,
  modifiers = {},
  onSelectedDate = Function.prototype,
  setSelectedDatepicker = Function.prototype,
  setFromControls = Function.prototype,
  onOpenDatepicker = Function.prototype,
  onCloseDatepicker = Function.prototype,
  InputComponent = TextField,
  sm = false,
  isVisibleByDefault = false,
  isSelectedDatepickerRange = false,
  OverlayComponent = null,
  tagId,
  inputRef,
  ...props
}) => {
  const {
    ref,
    isComponentVisible,
    setIsComponentVisible
  } = useComponentVisibleHook(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [{
    from,
    to
  }, setDateRange] = useState(selectedDaysRange);
  const [selectedTab, setSelectedTab] = useState(isSelectedDatepickerRange ? tabsList[1] : tabsList[0]);
  const isRangeTabs = selectedTab.key === 'range';
  const checkedDate = addDays({
    date: from,
    count: 2
  });
  const preparedSelectedDays = isRange ? [from, {
    from,
    to
  }] : selectedDaysSingle;
  const preparedValue = isRange || isRangeTabs ? {
    from,
    to
  } : selectedDaysSingle;
  const initialMonth = isRange ? from : selectedDaysSingle;
  const rangeSelectedDays = {
    start: from,
    end: to
  };
  const preparedModifiers = {
    ...modifiers,
    ...rangeSelectedDays
  };
  const onSelectTab = idx => setSelectedTab(tabsList[idx]);
  const onCloseModal = () => {
    setIsOpenModal(false);
    setFromControls(false);
  };
  const hidePicker = useCallback(() => {
    setIsComponentVisible(false);
    if (sm) onCloseModal();
  }, [sm, onCloseModal]);
  const setSingle = day => {
    onSelectedDate(day);
    hidePicker();
  };
  const setRange = day => {
    if (from && to && day >= from && day <= to) {
      onSelectedDate(datesRange);
      setDateRange(datesRange);
    }
    if (isSelectingFirstDay(from, to, day)) {
      const firstDate = {
        from: day,
        to: null
      };
      onSelectedDate(firstDate);
      setDateRange(firstDate);
    } else {
      const rangeDates = {
        from,
        to: day
      };
      onSelectedDate(rangeDates);
      setDateRange(rangeDates);
    }
  };
  const onDayChange = day => {
    if (isRange) {
      return setRange(day);
    }
    return setSingle(day);
  };
  useEffect(() => {
    if (from && to) {
      hidePicker();
    }
  }, [from, to]);
  useUpdateEffect(() => {
    if (!to && (!isComponentVisible || !isOpenModal)) {
      if (isDayAfter(checkedDate, lastDateForSearch)) {
        onSelectedDate(reverseTwoDaysFromDay(from));
        setDateRange(reverseTwoDaysFromDay(from));
      } else {
        onSelectedDate(addTwoDaysFromDay(from));
        setDateRange(addTwoDaysFromDay(from));
      }
    }
    if (isComponentVisible && !sm) {
      onOpenDatepicker({
        isOpenedDatepicker: true
      });
    }
    if (!isComponentVisible && !sm) {
      onOpenDatepicker({
        isOpenedDatepicker: false
      });
      onCloseDatepicker();
    }
  }, [isComponentVisible, isOpenModal]);
  useEffect(() => {
    if (isTabs) {
      setSelectedDatepicker(isRangeTabs);
    }
  }, []);
  useEffect(() => {
    if (isVisibleByDefault) {
      setIsComponentVisible(true);
    } else {
      setIsComponentVisible(false);
    }
  }, [isVisibleByDefault]);
  useEffect(() => {
    setSelectedDatepicker(isRangeTabs);
  }, [selectedTab]);
  // change current tab from outside
  useEffect(() => {
    if (!isVisibleByDefault) {
      if (isSelectedDatepickerRange !== (selectedTab.key === 'range')) {
        setSelectedTab(isSelectedDatepickerRange ? tabsList[1] : tabsList[0]);
      }
    }
  }, [isSelectedDatepickerRange]);
  // change range values from outside
  useEffect(() => {
    if (!isVisibleByDefault) {
      if (selectedDaysRange.from.getTime() !== from.getTime() || selectedDaysRange.to.getTime() !== to.getTime()) {
        setDateRange(selectedDaysRange);
      }
    }
  }, [selectedDaysRange]);
  const showPicker = useCallback(() => {
    setFromControls(false);
    if (sm) {
      setIsOpenModal(true);
    } else {
      setIsComponentVisible(true);
    }
  }, [sm, setFromControls]);
  const handleRootFocus = useCallback(() => {
    if (!isComponentVisible) {
      showPicker();
    }
  }, [isComponentVisible]);
  const isShownWithTabsDesktop = isTabs && !sm && isComponentVisible;
  const isShownWithoutTabsDesktop = !isTabs && !sm && isComponentVisible;
  const isShownWithTabsMobile = isTabs && isOpenModal && sm;
  const isShownWithoutTabsMobile = !isTabs && isOpenModal && sm;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    ref: ref
  }, /*#__PURE__*/React.createElement(CalendarIcon, {
    isOpenModal: isOpenModal
  }, /*#__PURE__*/React.createElement(ResponsiveValue, {
    value: preparedValue,
    isRange: isRange || isRangeTabs
  }, ({
    value,
    refInput
  }) => /*#__PURE__*/React.createElement(InputComponent, {
    id: tagId,
    labelName: labelInputName,
    value: inputValue && value,
    innerRef: useCallback(node => {
      /* eslint-disable-next-line no-param-reassign */refInput.current = node;
      if (inputRef) {
        /* eslint-disable-next-line no-param-reassign */inputRef.current = node;
      }
    }, []),
    className: "form-field--datepicker",
    autoComplete: "off",
    autoCorrect: "off",
    autoCapitalize: "off",
    onFocus: handleRootFocus,
    onClick: handleRootFocus,
    readOnly: true
  }))), /*#__PURE__*/React.createElement(DropdownAnimation, {
    isTriggered: isShownWithTabsDesktop
  }, /*#__PURE__*/React.createElement(DayPickerTabs, {
    tabs: tabsList,
    selectedTab: selectedTab,
    onSelect: onSelectTab,
    onDayChangeRange: setRange,
    onDayChangeSingle: setSingle,
    selectedDaysRange: {
      from,
      to
    },
    selectedDaysSingle: selectedDaysSingle,
    initialMonthRange: from,
    initialMonthSingle: selectedDaysSingle,
    numberOfMonths: numberOfMonths,
    disabledDays: disabledDays,
    modifiers: preparedModifiers,
    setIsComponentVisible: setIsComponentVisible,
    hasMonthControls: true,
    hasWeekDays: true,
    ...props
  })), /*#__PURE__*/React.createElement(DropdownAnimation, {
    isTriggered: isShownWithoutTabsDesktop
  }, /*#__PURE__*/React.createElement(DayPicker, {
    isRange: isRange,
    numberOfMonths: numberOfMonths,
    initialMonth: initialMonth,
    onDayClick: onDayChange,
    selectedDays: preparedSelectedDays,
    disabledDays: disabledDays,
    modifiers: preparedModifiers,
    OverlayComponent: OverlayComponent,
    isContainer: true,
    ...props
  }))), isShownWithoutTabsMobile && /*#__PURE__*/React.createElement(Modal, {
    isOpen: true,
    onClose: onCloseModal
  }, /*#__PURE__*/React.createElement(DayPicker, {
    isMobile: true,
    isRange: isRange,
    numberOfMonths: mobileNumberOfMonths,
    onDayClick: onDayChange,
    selectedDays: preparedSelectedDays,
    disabledDays: disabledDays,
    modifiers: preparedModifiers,
    OverlayComponent: OverlayComponent,
    onCloseModal: onCloseModal,
    hasMonthControls: false,
    hasWeekDays: false,
    ...props
  })), isShownWithTabsMobile && /*#__PURE__*/React.createElement(Modal, {
    isOpen: true,
    onClose: onCloseModal
  }, /*#__PURE__*/React.createElement(DayPickerTabs, {
    isMobile: true,
    tabs: tabsList,
    selectedTab: selectedTab,
    onSelect: onSelectTab,
    onDayChangeRange: setRange,
    onDayChangeSingle: setSingle,
    selectedDaysRange: {
      from,
      to
    },
    selectedDaysSingle: selectedDaysSingle,
    initialMonthRange: sm ? new Date() : from,
    initialMonthSingle: sm ? new Date() : selectedDaysSingle,
    numberOfMonths: mobileNumberOfMonths,
    disabledDays: disabledDays,
    modifiers: preparedModifiers,
    setIsComponentVisible: setIsComponentVisible,
    hasMonthControls: false,
    hasWeekDays: false,
    OverlayComponent: OverlayComponent,
    onCloseModal: onCloseModal,
    isSelectedDatepickerRange: isSelectedDatepickerRange,
    ...props
  })));
};
DatepickerInput.propTypes = {
  isTabs: bool,
  isRange: bool,
  sm: bool,
  isVisibleByDefault: bool,
  isSelectedDatepickerRange: bool,
  numberOfMonths: number,
  // deprecated
  label: string,
  inputValue: oneOfType([string, instanceOf(Date)]),
  selectedDaysSingle: instanceOf(Date),
  errors: array,
  selectedDaysRange: object,
  disabledDays: array,
  modifiers: object,
  onSelectedDate: func,
  onOpenDatepicker: func,
  onCloseDatepicker: func,
  setFromControls: func,
  setSelectedDatepicker: func,
  InputComponent: func,
  tagId: string,
  OverlayComponent: func,
  labelInputName: string,
  inputRef: object
};
export default responsive(/*#__PURE__*/memo(DatepickerInput), ['sm']);